<template>
  <div v-loading="loading" class="board-page">
    <p v-if="error" class="err">{{ error }}</p>
    <template v-else>
      <div class="board-panel">
        <div class="board-panel__filters">
          <TableFilters :items="filterItems" :value="filters" @input="onFiltersInput" />
          <el-button size="small" plain @click="clearFilters">清空</el-button>
          <span class="filters__count">命中 {{ filtered.length }} / {{ rows.length }} 条</span>
        </div>

        <div class="board-panel__board">
          <div class="lanes" role="list">
            <section
              v-for="lane in laneList"
              :key="lane.key"
              class="lane"
              :class="{ 'lane--collapsed': !lane.expanded, 'lane--empty': lane.count === 0 }"
              role="listitem"
            >
              <button
                type="button"
                class="lane__head"
                :aria-expanded="lane.expanded"
                @click="toggleLane(lane.key, lane.count)"
              >
                <i
                  class="lane__caret el-icon-arrow-down"
                  :class="{ 'lane__caret--folded': !lane.expanded }"
                  aria-hidden="true"
                />
                <span class="lane__dot" :style="{ background: lane.color }" aria-hidden="true" />
                <span class="lane__title" :title="lane.label">{{ lane.label }}</span>
                <span class="lane__count">{{ lane.count }}</span>
              </button>
              <div v-show="lane.expanded" class="lane__body">
                <p v-if="lane.count === 0" class="lane__empty">暂无</p>
                <button
                  v-for="row in lane.rows"
                  :key="row.id"
                  type="button"
                  class="card"
                  :style="{ borderLeftColor: lane.color }"
                  @click="openRow(row)"
                >
                  <div class="card__top">
                    <span class="card__part">{{ partTitle(row) }}</span>
                    <el-tag v-if="lane.overdueTag(row)" size="mini" type="danger">
                      {{ lane.overdueTag(row) }}
                    </el-tag>
                  </div>
                  <div class="card__meta">{{ boardMeta(row) }}</div>
                  <div class="card__desc">{{ row.yc || '—' }}</div>
                  <div v-if="cardFields(row).length" class="card__fields">
                    <div v-for="(line, idx) in cardFields(row)" :key="idx">
                      <b>{{ line.label }}：</b>
                      <span :class="{ 'card__hl': line.highlight }">{{ line.text }}</span>
                    </div>
                  </div>
                  <div class="card__foot">
                    <span class="card__foot-left">负责人 {{ row.fzr || '—' }}</span>
                    <span v-if="filled(row.planDate)" class="card__foot-right">
                      完成计划 {{ shortPlan(row.planDate) }}
                    </span>
                  </div>
                </button>
              </div>
            </section>
          </div>
        </div>
      </div>
      <LiveCaseDrawer
        :visible="drawerOpen"
        :row="activeRow"
        :option-cases="rows"
        @close="drawerOpen = false"
        @saved="onSaved"
      />
    </template>
  </div>
</template>

<script lang="ts">
import Vue from 'vue';

import { LiveCaseDrawer } from '@/components';
import { TableFilters } from '@/components/crud';
import type { FilterItem } from '@/components/crud/types';
import { fetchLiveCases, initialLiveCasesLoadState, peekLiveCasesCache, type LiveCase } from '@/api/live-case';
import { liveLanes } from '@/domain/live-case';
import {
  complaintBoardMeta,
  complaintCardFieldLines,
  complaintPartTitle,
  complaintShortPlan,
} from '@/domain/complaint-present';
import { SIM_TODAY } from '@/utils/time';

import {
  buildYearMonthFilterItems,
  matchesYearMonthFilter,
  normalizeYearMonthFilters,
  pickDefaultYearMonth,
} from '../live-editor/filters';

type LaneKey = 'unreplied' | 'handling' | 'defect';

interface LiveBoardFilters {
  q: string;
  deptP: string;
  year: string[];
  month: string[];
}

interface LaneMeta {
  key: LaneKey;
  label: string;
  color: string;
}

const LANES: LaneMeta[] = [
  { key: 'unreplied', label: '未回复', color: '#d64545' },
  { key: 'handling', label: '处理中', color: '#3b6fe0' },
  { key: 'defect', label: '不良品未处理', color: '#d9822b' },
];

type LaneExpandMode = 'auto' | boolean;

function matchKeyword(row: LiveCase, keyword: string): boolean {
  const q = keyword.trim().toLowerCase();
  if (!q) return true;
  const hay = [row.pm, row.lh, row.yc, row.fzr, row.jz].join(' ').toLowerCase();
  return hay.includes(q);
}

function filled(value: string | null | undefined): boolean {
  return typeof value === 'string' && value.trim().length > 0;
}

export default Vue.extend({
  name: 'LiveBoard',
  components: { LiveCaseDrawer, TableFilters },
  data() {
    const laneExpand: Record<LaneKey, LaneExpandMode> = {
      unreplied: 'auto',
      handling: 'auto',
      defect: 'auto',
    };
    const initial = initialLiveCasesLoadState();
    return {
      loading: initial.loading,
      error: '',
      rows: initial.rows,
      filters: { q: '', deptP: '', year: [], month: [] } as LiveBoardFilters,
      drawerOpen: false,
      activeRow: null as LiveCase | null,
      laneExpand,
    };
  },
  computed: {
    filterItems(): FilterItem[] {
      const deptOptions = Array.from(new Set(this.rows.map((row) => row.deptP).filter(Boolean))).sort();
      return [
        ...buildYearMonthFilterItems(this.rows, this.filters),
        {
          key: 'q',
          type: 'input',
          placeholder: '搜索料号、品名、机种、异常、负责人',
          width: '260px',
        },
        {
          key: 'deptP',
          type: 'select',
          placeholder: '全部生产部门',
          width: '160px',
          options: deptOptions.map((value) => ({ label: value, value })),
        },
      ];
    },
    filtered(): LiveCase[] {
      return this.rows.filter((row) => {
        if (!matchesYearMonthFilter(row, this.filters)) return false;
        if (this.filters.deptP && row.deptP !== this.filters.deptP) return false;
        return matchKeyword(row, this.filters.q);
      });
    },
    laneStateById(): Map<string, ReturnType<typeof liveLanes>> {
      const map = new Map<string, ReturnType<typeof liveLanes>>();
      this.filtered.forEach((row) => {
        map.set(row.id, liveLanes(row, SIM_TODAY));
      });
      return map;
    },
    laneList(): Array<
      LaneMeta & {
        count: number;
        rows: LiveCase[];
        expanded: boolean;
        overdueTag: (row: LiveCase) => string | null;
      }
    > {
      const buckets: Record<LaneKey, LiveCase[]> = {
        unreplied: [],
        handling: [],
        defect: [],
      };
      this.filtered.forEach((row) => {
        const state = this.laneStateById.get(row.id);
        if (!state) return;
        if (state.unreplied) buckets.unreplied.push(row);
        if (state.handling) buckets.handling.push(row);
        if (state.defectOpen) buckets.defect.push(row);
      });
      const overdueTag = (key: LaneKey, row: LiveCase): string | null => {
        const state = this.laneStateById.get(row.id);
        if (!state) return null;
        if (key === 'unreplied' && state.replyOverdue) return '超期未回复';
        if (key === 'handling' && state.closeOverdue) return '超期未结案';
        if (key === 'defect' && state.replyOverdue) return '超期';
        return null;
      };
      return LANES.map((lane) => {
        const rows = buckets[lane.key]
          .slice()
          .sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
        const count = rows.length;
        return {
          ...lane,
          count,
          rows,
          expanded: this.isLaneExpanded(lane.key, count),
          overdueTag: (row: LiveCase) => overdueTag(lane.key, row),
        };
      });
    },
  },
  created() {
    this.load();
  },
  methods: {
    load() {
      if (!peekLiveCasesCache()) this.loading = true;
      fetchLiveCases()
        .then((rows) => {
          this.rows = rows;
          this.error = '';
          const defaults = pickDefaultYearMonth(rows);
          this.filters = {
            ...this.filters,
            ...normalizeYearMonthFilters(rows, {
              year: defaults.year,
              month: [],
            }),
          };
        })
        .catch((err: Error) => {
          this.error = err.message;
        })
        .then(() => {
          this.loading = false;
        });
    },
    onFiltersInput(value: LiveBoardFilters) {
      const { year, month } = normalizeYearMonthFilters(this.rows, value);
      this.filters = { ...value, year, month };
    },
    clearFilters() {
      const defaults = pickDefaultYearMonth(this.rows);
      this.filters = {
        q: '',
        deptP: '',
        year: defaults.year,
        month: [],
      };
    },
    isLaneExpanded(key: LaneKey, count: number): boolean {
      const mode = this.laneExpand[key];
      if (mode === 'auto') return count > 0;
      return mode;
    },
    toggleLane(key: LaneKey, count: number) {
      const next = !this.isLaneExpanded(key, count);
      this.$set(this.laneExpand, key, next);
    },
    partTitle(row: LiveCase): string {
      return complaintPartTitle(row);
    },
    boardMeta(row: LiveCase): string {
      return complaintBoardMeta(row);
    },
    cardFields(row: LiveCase) {
      return complaintCardFieldLines(row);
    },
    shortPlan(value: string | null): string {
      return complaintShortPlan(value);
    },
    filled,
    openRow(row: LiveCase) {
      this.activeRow = row;
      this.drawerOpen = true;
    },
    onSaved(row: LiveCase) {
      const index = this.rows.findIndex((item) => item.id === row.id);
      if (index >= 0) this.$set(this.rows, index, row);
      this.activeRow = row;
    },
  },
});
</script>

<style lang="scss" scoped>
.board-page {
  @include list-page-layout;
  color: $color-text;
  font-family: 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

.err {
  color: $color-danger;
  font-size: 14px;
}

.board-panel {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
  padding: 16px 24px 14px;
  border: 1px solid $color-border;
  border-radius: $border-radius-base;
  background: $color-surface;
}

.board-panel__filters {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  width: 100%;
  min-height: 32px;
}

.filters__count {
  margin-left: auto;
  color: $color-text-secondary;
  font-size: 12px;
}

.board-panel__board {
  display: flex;
  flex: 1;
  flex-direction: column;
  min-height: 0;
  border: 1px solid $color-border;
  border-radius: $border-radius-base;
  background: $color-surface;
  overflow: hidden;
}

.lanes {
  display: flex;
  flex: 1;
  gap: 8px;
  min-height: 0;
  padding: 10px 12px;
  overflow-x: auto;
  overflow-y: hidden;
}

.lane {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-width: 280px;
  max-width: 360px;
  min-height: 0;
  border: 1px solid $color-border;
  border-radius: 8px;
  background: $color-surface-muted;

  &--collapsed {
    flex: 0 0 44px;
    min-width: 44px;
    max-width: 44px;
  }
}

.lane__head {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  gap: 6px;
  width: 100%;
  margin: 0;
  padding: 10px 10px 8px;
  border: 0;
  border-bottom: 1px solid $color-hairline;
  background: $color-surface;
  border-radius: 8px 8px 0 0;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:hover {
    background: #f8fafc;
  }
}

.lane--collapsed .lane__head {
  flex: 1;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  padding: 12px 4px;
  border-bottom: 0;
  border-radius: 8px;
}

.lane__caret {
  flex-shrink: 0;
  font-size: 12px;
  color: $color-text-secondary;
  transition: transform 0.15s ease;

  &--folded {
    transform: rotate(-90deg);
  }
}

.lane--collapsed .lane__caret {
  transform: rotate(-90deg);
}

.lane__dot {
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 50%;
}

.lane--collapsed .lane__dot {
  display: none;
}

.lane__title {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-weight: 600;
  color: $color-text;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.lane--collapsed .lane__title {
  flex: 1;
  max-height: none;
  writing-mode: vertical-rl;
  text-orientation: mixed;
  letter-spacing: 0.12em;
  white-space: normal;
  line-height: 1.35;
  text-align: center;
}

.lane__count {
  flex-shrink: 0;
  min-width: 22px;
  padding: 0 6px;
  border-radius: 10px;
  background: $color-primary-light;
  color: $color-primary;
  font-size: 12px;
  font-weight: 600;
  line-height: 20px;
  text-align: center;
}

.lane--collapsed .lane__count {
  margin-top: auto;
}

.lane__body {
  flex: 1;
  min-height: 0;
  padding: 8px;
  overflow-x: hidden;
  overflow-y: auto;
}

.lane__empty {
  margin: 0;
  padding: 16px 8px;
  color: $color-text-secondary;
  font-size: 12px;
  text-align: center;
}

.card {
  display: block;
  width: 100%;
  margin-bottom: 8px;
  padding: 10px 12px;
  border: 1px solid $color-border;
  border-left-width: 3px;
  border-radius: 6px;
  background: $color-surface;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    border-color: #c5cdd8;
    box-shadow: 0 1px 4px rgba(31, 39, 51, 0.06);
  }
}

.card__top {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 6px;
}

.card__part {
  font-size: 13px;
  font-weight: 600;
  line-height: 1.35;
}

.card__meta {
  margin-top: 6px;
  color: $color-text-secondary;
  font-size: 11px;
  line-height: 1.4;
}

.card__desc {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.45;
  color: $color-text-regular;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.card__fields {
  margin-top: 6px;
  font-size: 11px;
  line-height: 1.45;
  color: $color-text-secondary;

  b {
    color: $color-text-regular;
    font-weight: 600;
  }
}

.card__hl {
  color: $color-danger;
}

.card__foot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 8px;
  margin-top: 8px;
  padding-top: 6px;
  border-top: 1px solid $color-hairline;
  font-size: 11px;
  color: $color-text-secondary;
}

.card__foot-left {
  flex: 1;
  min-width: 0;
}

.card__foot-right {
  flex-shrink: 0;
  text-align: right;
}
</style>
